import { CurrencyTextField } from '@/common/components/CurrencyTextField';
import Modal from '@/common/components/Modal';
import SmallBadge from '@/common/components/SmallBadge';
import { UnitTextField } from '@/common/components/UnitTextField';
import { formatCurrency } from '@/common/utils/formatters';
import { unFormatCurrency, unFormatUnknownUnit } from '@/common/utils/unFormatters';
import ProposalAwareResumeItem from '@/features/maintenance/features/workflows/pages/Show/components/tabs/ForwardingsTab/sections/DirectiveEntry/entries/ExecutionEntry/content/ExecutionEntryDiagnostic/ProposalAwareResumeItem';
import { ReactNode } from 'react';

interface EditProposalModalProps {
  title: string
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  onConfirm: () => void
  localUnitPrice: string
  setLocalUnitPrice: (value: string) => void
  localWarranty: string
  setLocalWarranty: (value: string) => void
  originalUnitPrice: number
  originalTotalValue: number
  originalWarranty: number
  previewTotalValue: number
  unitPriceLabel: string
  quantityDisplay: ReactNode
  disabled?: boolean
}

const EditProposalModal = ({
  title,
  isOpen,
  setIsOpen,
  onConfirm,
  localUnitPrice,
  setLocalUnitPrice,
  localWarranty,
  setLocalWarranty,
  originalUnitPrice,
  originalTotalValue,
  originalWarranty,
  previewTotalValue,
  unitPriceLabel,
  quantityDisplay,
  disabled,
}: EditProposalModalProps) => {
  const localUnitPriceNumber = unFormatCurrency(localUnitPrice)
  const localWarrantyNumber = unFormatUnknownUnit(localWarranty)

  const isUnitPriceModified = !!localUnitPrice && localUnitPriceNumber !== originalUnitPrice
  const isWarrantyModified = !!localWarranty && localWarrantyNumber !== originalWarranty

  const displayNewTotalValue = isUnitPriceModified ? previewTotalValue : null

  const totalValuePercentage =
    displayNewTotalValue !== null && originalTotalValue !== 0
      ? ((displayNewTotalValue - originalTotalValue) / originalTotalValue).toString()
      : null

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      triggerAsChild
      Trigger={
        <SmallBadge
          text="Editar"
          className="bg-primary-blue hover:bg-primary-blue-hover transition-colors cursor-pointer"
        />
      }
      title={title}
      body={
        <div className="flex flex-col gap-3">
          <div className="flex gap-3">
            <div className="flex flex-col gap-0.5 flex-1">
              <CurrencyTextField
                forceValue
                label={unitPriceLabel}
                disabled={disabled}
                value={localUnitPrice}
                onChange={(e: any) => setLocalUnitPrice(e.target.value)}
              />

              {isUnitPriceModified && (
                <p className="text-xs text-yellow-400">Valor do diagnóstico: {formatCurrency(originalUnitPrice)}</p>
              )}
            </div>

            <div className="flex gap-3 mt-5">
              <p className="text-sm mt-3">x</p>

              {quantityDisplay}

              <p className="text-sm mt-3">=</p>

              <ProposalAwareResumeItem
                label="Valor total"
                originalValue={originalTotalValue}
                newValue={displayNewTotalValue}
                differencePercentage={totalValuePercentage}
                format={formatCurrency}
              />
            </div>
          </div>

          <div className="flex flex-col gap-0.5">
            <UnitTextField
              forceValue
              label="Garantia em dias"
              unit="dia(s)"
              decimals={0}
              className="flex-1"
              disabled={disabled}
              value={localWarranty}
              onChange={(e: any) => setLocalWarranty(e.target.value)}
            />

            {isWarrantyModified && (
              <p className="text-xs text-yellow-400">Garantia do diagnóstico: {originalWarranty} dias</p>
            )}
          </div>
        </div>
      }
      useCancellableFooter
      confirmButtonText="Salvar"
      onConfirm={onConfirm}
      cancelButtonClassName="bg-white-200 rounded-es-lg"
      confirmButtonClassName="bg-blue-700 rounded-ee-lg"
    />
  )
}

export default EditProposalModal
